import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { SourceImages } from "@/components/create/SourceImages";
import { ComingSoonHero, QaNotice } from "@/components/create/StudioNotices";
import { WorkflowGallery } from "@/components/create/WorkflowGallery";
import { StatusBadge } from "@/components/create/ModelPicker";
import { StudioHeader } from "@/components/create/explore/StudioHeader";
import { api } from "@/lib/api";
import { hfOpen, useCharacters, useHfCatalog, useMediaConfig, usePresets } from "@/lib/studio";
import { INFLUENCER_FLOWS } from "@/lib/workflows";
import { cn } from "@/lib/utils";

function NewCharacter({ onDone }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [refs, setRefs] = useState([]);
  const save = useMutation({
    mutationFn: () => api.post("/studio/characters", { name: name.trim(), image_ids: refs.map((r) => r.id) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["studio-characters"] }); toast.success("Character saved"); onDone(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <div className="space-y-3 rounded-2xl border border-white/10 bg-ink-900 p-4" data-testid="new-character-form">
      <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="Character name" aria-label="Character name" data-testid="character-name-input"
        className="h-11 w-full rounded-xl border border-white/10 bg-ink-950 px-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-white/25 focus:outline-none" />
      <p className="text-[11px] text-zinc-500">Identity references · 1–4 clear photos of the same person (yours or with their consent)</p>
      <SourceImages sources={refs} onChange={setRefs} max={4} testId="character-refs" />
      <button type="button" disabled={!name.trim() || !refs.length || save.isPending} onClick={() => save.mutate()} data-testid="character-save-button"
        className="flex h-10 items-center gap-2 rounded-full bg-white px-5 text-sm font-semibold text-ink-950 disabled:bg-white/10 disabled:text-zinc-500">
        {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Save character
      </button>
    </div>
  );
}

function Characters({ selected, onSelect }) {
  const qc = useQueryClient();
  const list = useCharacters();
  const [adding, setAdding] = useState(false);
  const [armed, setArmed] = useState(null);
  const remove = useMutation({
    mutationFn: (id) => api.del(`/studio/characters/${id}`),
    onSuccess: () => { setArmed(null); qc.invalidateQueries({ queryKey: ["studio-characters"] }); toast.success("Character deleted"); },
    onError: (e) => toast.error(e.message),
  });
  const items = list.data?.items || [];
  return (
    <section className="space-y-3" data-testid="characters-section">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold tracking-tight text-white">Your characters</h2>
        <button type="button" onClick={() => setAdding((a) => !a)} className="flex items-center gap-1 text-xs font-medium text-zinc-300 hover:text-white" data-testid="character-add-button"><Plus className="h-4 w-4" /> New</button>
      </div>
      {adding && <NewCharacter onDone={() => setAdding(false)} />}
      {!items.length && !adding && <p className="text-sm text-zinc-500" data-testid="characters-empty">Save a character from a few reference photos to keep the same identity across images and videos.</p>}
      <div className="-mx-4 flex gap-3 overflow-x-auto px-4 pb-1 [scrollbar-width:none] sm:mx-0 sm:px-0">
        {items.map((c) => (
          <div key={c.id} className={cn("relative w-28 shrink-0 overflow-hidden rounded-2xl border", selected?.id === c.id ? "border-white/60" : "border-white/10")} data-testid={`character-${c.id}`}>
            <button type="button" onClick={() => onSelect(c)} className="block w-full text-left">
              <img src={c.reference_urls[0]} alt={c.name} className="aspect-[3/4] w-full object-cover" />
              <span className="block truncate px-2 py-1.5 text-xs font-medium text-zinc-200">{c.name}</span>
            </button>
            {selected?.id === c.id && <Check className="absolute right-2 top-2 h-5 w-5 rounded-full bg-white p-1 text-ink-950" />}
            <button type="button" onClick={() => (armed === c.id ? remove.mutate(c.id) : setArmed(c.id))} aria-label={armed === c.id ? `Tap again to delete ${c.name}` : `Delete ${c.name}`} className={cn("absolute left-2 top-2 flex items-center gap-1 rounded-full bg-black/70 p-1 text-[10px]", armed === c.id ? "pr-2 text-red-300" : "text-zinc-300 hover:text-red-300")} data-testid={`character-delete-${c.id}`}><Trash2 className="h-3 w-3" />{armed === c.id && "Delete?"}</button>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function InfluencerPage() {
  const config = useMediaConfig().data;
  const soulId = (useHfCatalog().data?.models || []).find((m) => m.operation === "character");
  const covers = (usePresets().data?.items || []).filter((p) => p.category === "Portraits" || p.operation === "edit").map((p) => p.cover_url);
  const [character, setCharacter] = useState(null);
  const open = hfOpen(config);
  return (
    <div className="h-full overflow-y-auto" data-testid="influencer-page">
      <StudioHeader title="Influencer" back />
      <div className="mx-auto w-full max-w-6xl space-y-8 px-4 pb-20 pt-5 sm:px-6 sm:pt-8">
        {config && !open && <ComingSoonHero covers={covers} testId="influencer-coming-soon" title="AI Influencer opens soon" body="Save characters from reference photos, make character sheets and keep the same identity across images and videos." />}
        {open && config.higgsfield.access === "qa" && <QaNotice budget={config.image.qa_budget} tools="AI Influencer" />}
        {open && <Characters selected={character} onSelect={setCharacter} />}
        {open && (
          <section className="space-y-3">
            <h2 className="text-lg font-bold tracking-tight text-white">Consistency workflows</h2>
            <WorkflowGallery flows={INFLUENCER_FLOWS} needsRefNote="Pick one of your characters above, or add a reference photo below."
              refsFor={() => (character ? [{ id: character.id, preview_url: character.reference_urls[0] }] : null)} />
          </section>
        )}
        {open && soulId && (
          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-white/[0.08] p-3 text-xs text-zinc-500" data-testid="soul-id-status">
            <span className="font-medium text-zinc-300">Soul ID character training</span> <StatusBadge status={soulId.status} /> {soulId.status_reason}
          </div>
        )}
      </div>
    </div>
  );
}
